import React, { useRef } from 'react';
import { Box, Container, Typography, Grid, Fade, Accordion, AccordionSummary, AccordionDetails, Paper, Stack, Chip, Avatar, Rating, Card, CardContent, CardHeader, Divider, Button, IconButton } from '@mui/material';
import { LocalShipping, Home as HomeIcon, Business, Inventory, ExpandMore, Star, Groups, Handshake, Speed, Calculate, Shield, ThumbUp, EventAvailable, LocationOn, WorkspacePremium, CheckCircleOutline, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <HomeIcon sx={{ fontSize: 50, color: '#ffd700' }} />,
      title: 'Privatumzüge',
      description: 'Professionelle Umzüge für Ihre Wohnung oder Ihr Haus. Wir kümmern uns um alles von A bis Z.'
    },
    {
      icon: <Business sx={{ fontSize: 50, color: '#ffd700' }} />,
      title: 'Firmenumzüge',
      description: 'Reibungslose Büro- und Firmenumzüge mit minimaler Ausfallzeit für Ihr Unternehmen.'
    },
    {
      icon: <Inventory sx={{ fontSize: 50, color: '#ffd700' }} />,
      title: 'Verpackungsservice',
      description: 'Sichere Verpackung Ihrer wertvollen Gegenstände mit professionellem Verpackungsmaterial.'
    },
    {
      icon: <LocalShipping sx={{ fontSize: 50, color: '#ffd700' }} />,
      title: 'Transportservice',
      description: 'Zuverlässiger Transport mit modernen Fahrzeugen und erfahrenen Fahrern.'
    }
  ];

  const whyChooseUs = [
    {
      icon: <Star />,
      title: 'Erfahrung',
      description: 'Über 10 Jahre erfolgreiche Umzugserfahrung',
      stat: '10+'
    },
    {
      icon: <Groups />,
      title: 'Professionelles Team',
      description: 'Geschulte und erfahrene Umzugsmitarbeiter täglich',
      stat: '50+'
    },
    {
      icon: <Handshake />,
      title: 'Faire Preise',
      description: 'Transparente Preise ohne versteckte Zusatzkosten',
      stat: '100%'
    },
    {
      icon: <Speed />,
      title: 'Schnell & Zuverlässig',
      description: 'Pünktliche und effiziente Umzugsdurchführung täglich',
      stat: '24/7'
    }
  ];

  const faqs = [
    { question: 'Wann sollte ich meinen Umzug buchen?', answer: 'Mindestens 4–6 Wochen im Voraus, besonders in der Hochsaison.' },
    { question: 'Wie wird der Preis berechnet?', answer: 'Abhängig von Volumen, Entfernung, Stockwerk, Zusatzleistungen und Parkmöglichkeiten.' },
    { question: 'Ist eine Versicherung im Preis enthalten?', answer: 'Ja – gesetzlich bis 620 € pro m³, Zusatzversicherung auf Wunsch.' },
    { question: 'Kann die Firma Möbelmontage übernehmen?', answer: 'Ja – Demontage und Montage sind als Service buchbar.' },
    { question: 'Wie viele Kartons brauche ich?', answer: 'Durchschnittlich 2–3 Kartons pro Kubikmeter Umzugsgut.' },
    { question: 'Organisiert die Firma Halteverbotszonen?', answer: 'Ja – auf Wunsch wird die Genehmigung beantragt und beschildert.' },
    { question: 'Wie bekomme ich ein Angebot?', answer: 'Nach Besichtigung vor Ort oder per detaillierter Telefon-/Onlineanfrage.' },
    { question: 'Was passiert mit Möbeln, die nicht mitziehen?', answer: 'Verkaufen, spenden oder einlagern – oft mit Hilfe der Firma.' },
    { question: 'Kann ich beim Packen Hilfe bekommen?', answer: 'Ja – professionelle Packservices mit geeignetem Material sind möglich.' },
    { question: 'Wie erkenne ich ein seriöses Umzugsunternehmen?', answer: 'Transparente Preise, gute Bewertungen, Zertifikate und keine versteckten Kosten.' }
  ];

  // New content arrays
  const stats = [
    { icon: <WorkspacePremium sx={{ color: '#ffd700' }} />, label: '10+ Jahre', sub: 'Erfahrung' },
    { icon: <ThumbUp sx={{ color: '#55d6dc' }} />, label: '1.000+', sub: 'zufriedene Kunden' },
    { icon: <Shield sx={{ color: '#55d6dc' }} />, label: 'Voll versichert', sub: 'Sicher unterwegs' },
    { icon: <Speed sx={{ color: '#ffd700' }} />, label: 'Schnell & pünktlich', sub: 'Deutschlandweit' }
  ];

  const steps = [
    {
      title: 'Anfrage & Termin',
      desc: 'Senden Sie uns Ihre Anfrage – wir melden uns schnell mit einem passenden Termin.',
      icon: <EventAvailable sx={{ color: '#ffd700' }} />
    },
    {
      title: 'Packen & Vorbereiten',
      desc: 'Wir stellen Material bereit oder übernehmen das professionelle Verpacken.',
      icon: <Inventory sx={{ color: '#55d6dc' }} />
    },
    {
      title: 'Transport & Lieferung',
      desc: 'Sicherer Transport mit modernen Fahrzeugen und geschultem Team.',
      icon: <LocalShipping sx={{ color: '#55d6dc' }} />
    },
    {
      title: 'Aufbau & Abnahme',
      desc: 'Montage der Möbel und finale Abnahme – fertig!',
      icon: <Handshake sx={{ color: '#ffd700' }} />
    }
  ];

  const testimonials = [
    {
      name: 'Ali B.',
      city: 'Berlin',
      rating: 5,
      text:
        'Sehr professionell, pünktlich und freundlich. Unser Umzug war schneller erledigt als gedacht.'
    },
    {
      name: 'Merve K.',
      city: 'Hamburg',
      rating: 4.5,
      text:
        'Top Team! Sorgfältig verpackt und nichts beschädigt. Absolute Empfehlung.'
    },
    {
      name: 'Jonas R.',
      city: 'München',
      rating: 5,
      text:
        'Transparente Preise und super Service. Der Ablauf war stressfrei und organisiert.'
    }
  ];

  const cities = [
    'Berlin', 'Hamburg', 'München', 'Köln', 'Frankfurt', 'Stuttgart', 'Düsseldorf',
    'Dortmund', 'Essen', 'Leipzig', 'Hannover', 'Bremen', 'Nürnberg', 'Dresden',
    'Bochum', 'Wuppertal', 'Bonn', 'Münster'
  ];

  const plans = [
    {
      name: 'Basic',
      price: 'ab 299 €',
      highlight: false,
      features: ['Transport innerhalb der Stadt', 'Tragen bis 2. Etage', 'Grundversicherung inklusive']
    },
    {
      name: 'Komfort',
      price: 'ab 599 €',
      highlight: true,
      features: ['Alles aus Basic', 'Demontage & Montage', 'Halteverbotszone (optional)', 'Zusatzversicherung verfügbar']
    },
    {
      name: 'Premium',
      price: 'ab 999 €',
      highlight: false,
      features: ['Alles aus Komfort', 'Komplettpackservice', 'Express-Termin möglich', 'Einlagerung (optional)']
    }
  ];

  // Carousel controls for testimonials
  const carouselRef = useRef(null);
  const scroll = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: { xs: '65vh', md: '90vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box textAlign="center">
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', md: '5rem' },
                  fontWeight: 'bold',
                  mb: 3,
                  color: 'white'
                }}
              >
                H&N Umzugsservice
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 4,
                  color: '#55d6dc'
                }}
              >
                Ihr zuverlässiger Partner für Umzüge in Deutschland
              </Typography>
              <Typography variant="h6" sx={{ mb: 5, color: 'white', opacity: 0.9 }}>
                Professionell • Schnell • Günstig
              </Typography>
              <Box
                component="button"
                onClick={() => navigate('/contact')}
                style={{
                  backgroundColor: '#55d6dc',
                  color: '#275664',
                  padding: '12px 32px',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
              >
                Kostenloses Angebot
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Services Section moved near FAQ */}

      {/* About Section moved near FAQ */}

      {/* Trust / Stats Strip */}
      <Box
        sx={{
          py: 6,
          bgcolor: '#0f2832',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Stack alignItems="center" spacing={1.2}>
                  <Box sx={{ display: 'inline-flex', p: 1.2, bgcolor: 'rgba(85,214,220,0.12)', borderRadius: '50%' }}>
                    {s.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>{s.label}</Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.7 }}>{s.sub}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 6, fontWeight: 'bold', color: 'white' }}
          >
            So funktioniert's
          </Typography>
          <Grid container spacing={3}>
            {steps.map((step, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#1a3a44',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                    height: '100%'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ display: 'inline-flex', p: 1, bgcolor: 'rgba(255,215,0,0.12)', borderRadius: '50%' }}>
                      {step.icon}
                    </Box>
                    <Typography variant="overline" sx={{ color: '#ffd700', fontWeight: 700 }}>
                      Schritt {idx + 1}
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ mt: 1.5, color: 'white', fontWeight: 700 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'white', opacity: 0.85 }}>
                    {step.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Calculator CTA */}
      <Box sx={{ py: 8, bgcolor: '#275664' }}>
        <Container maxWidth="lg">
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              bgcolor: '#1a3a44',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ display: 'inline-flex', p: 1.5, bgcolor: 'rgba(85,214,220,0.12)', borderRadius: '50%' }}>
                    <Calculate sx={{ color: '#55d6dc' }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Umzugskosten sofort kalkulieren
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                      Nutzen Sie unseren Online-Kalkulator und erhalten Sie in wenigen Minuten eine Preisschätzung.
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  <Button
                    variant="contained"
                    onClick={() => navigate('/calculator')}
                    sx={{ bgcolor: '#55d6dc', color: '#275664', fontWeight: 'bold', '&:hover': { bgcolor: '#4bc2c8' } }}
                  >
                    Zum Kalkulator
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/contact')}
                    sx={{ borderColor: '#ffd700', color: '#ffd700', '&:hover': { borderColor: '#ffd700', bgcolor: 'rgba(255,215,0,0.08)' } }}
                  >
                    Angebot anfordern
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Testimonials - Carousel style using Paper elevation */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 6, fontWeight: 'bold', color: 'white' }}
          >
            Kundenstimmen
          </Typography>

          <Box>
            <Box
              ref={carouselRef}
              sx={{
                display: 'flex',
                gap: 2.5,
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth',
                px: { xs: 1, md: 0 },
                pb: 1,
                '&::-webkit-scrollbar': { height: 8 },
                '&::-webkit-scrollbar-thumb': { background: '#55d6dc', borderRadius: 8 },
                '& > *': { scrollSnapAlign: 'center' }
              }}
            >
              {testimonials.map((t, i) => (
                <Paper
                  key={i}
                  elevation={4}
                  className="MuiPaper-root MuiPaper-elevation"
                  sx={{
                    minWidth: { xs: '85%', sm: 420, md: 460 },
                    flex: '0 0 auto',
                    bgcolor: '#1a3a44',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                    p: { xs: 2.5, md: 3 }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#55d6dc', color: '#275664' }}>{t.name.charAt(0)}</Avatar>
                    <Box>
                      <Typography sx={{ color: 'white', fontWeight: 700 }}>{t.name}</Typography>
                      <Typography sx={{ color: 'white', opacity: 0.7 }}>{t.city}</Typography>
                    </Box>
                  </Stack>
                  <Rating value={t.rating} precision={0.5} readOnly sx={{ color: '#ffd700', mt: 1.5 }} />
                  <Typography variant="body2" sx={{ mt: 1.5, color: 'white', opacity: 0.9, lineHeight: 1.7 }}>
                    {t.text}
                  </Typography>
                </Paper>
              ))}
            </Box>

            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 2 }}>
              <IconButton
                aria-label="zurück"
                onClick={() => scroll(-1)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
                }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                aria-label="weiter"
                onClick={() => scroll(1)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
                }}
              >
                <ChevronRight />
              </IconButton>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Service Area */}
      <Box sx={{ py: 8, bgcolor: '#275664' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ mb: 3, fontWeight: 'bold', color: 'white' }}>
            Unser Einsatzgebiet
          </Typography>
          <Typography align="center" sx={{ mb: 4, color: 'white', opacity: 0.8 }}>
            Deutschlandweit unterwegs – Schwerpunkte in großen Metropolregionen.
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
            {cities.map((c) => (
              <Chip
                key={c}
                icon={<LocationOn sx={{ color: '#ffd700' }} />}
                label={c}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }
                }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Pricing */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 6, fontWeight: 'bold', color: 'white' }}
          >
            Preispakete
          </Typography>
          <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mx: 'auto', maxWidth: { xs: 600, sm: 900, md: 1100 } }}>
            {plans.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p.name} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  sx={{
                    height: '100%',
                    width: '100%',
                    maxWidth: 360,
                    mx: 'auto',
                    bgcolor: '#1a3a44',
                    borderRadius: 2,
                    border: p.highlight ? '2px solid #ffd700' : '1px solid rgba(255,255,255,0.08)',
                    boxShadow: p.highlight ? '0 8px 24px rgba(255,215,0,0.12)' : 'none'
                  }}
                >
                  <CardHeader
                    title={<Typography sx={{ color: 'white', fontWeight: 800 }}>{p.name}</Typography>}
                    subheader={<Typography sx={{ color: '#55d6dc', fontWeight: 700 }}>{p.price}</Typography>}
                  />
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
                  <CardContent>
                    <Stack spacing={1.2}>
                      {p.features.map((f, i) => (
                        <Stack direction="row" spacing={1} alignItems="center" key={i}>
                          <CheckCircleOutline sx={{ color: '#55d6dc' }} />
                          <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>{f}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <Button
                      fullWidth
                      variant={p.highlight ? 'contained' : 'outlined'}
                      onClick={() => navigate('/contact')}
                      sx={{
                        mt: 3,
                        bgcolor: p.highlight ? '#ffd700' : 'transparent',
                        color: p.highlight ? '#275664' : '#ffd700',
                        borderColor: '#ffd700',
                        fontWeight: 'bold',
                        '&:hover': {
                          bgcolor: p.highlight ? '#e6c200' : 'rgba(255,215,0,0.12)',
                          borderColor: '#ffd700'
                        }
                      }}
                    >
                      Angebot anfragen
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: 10, bgcolor: '#0a1f28' }}>
        <Container maxWidth="lg">
          <Paper
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              bgcolor: '#0f2832',
              border: '1px solid rgba(255,255,255,0.08)',
              backgroundImage: 'linear-gradient(135deg, rgba(85,214,220,0.06), rgba(255,215,0,0.06))'
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                  Bereit für einen stressfreien Umzug?
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', opacity: 0.85 }}>
                  Wir übernehmen Planung, Verpackung, Transport und Montage – zuverlässig und termintreu.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  <Button
                    variant="contained"
                    onClick={() => navigate('/contact')}
                    sx={{ bgcolor: '#55d6dc', color: '#275664', fontWeight: 'bold', '&:hover': { bgcolor: '#4bc2c8' } }}
                  >
                    Jetzt Kontakt aufnehmen
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/services')}
                    sx={{ borderColor: '#ffd700', color: '#ffd700', '&:hover': { borderColor: '#ffd700', bgcolor: 'rgba(255,215,0,0.08)' } }}
                  >
                    Leistungen ansehen
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Services Section (moved) */}
      <Box sx={{ py: 10, bgcolor: '#275664' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 6, fontWeight: 'bold', color: 'white' }}
          >
            Unsere Leistungen
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <Fade in timeout={1000 + index * 200}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      bgcolor: '#1a3a44',
                      borderRadius: 2,
                      p: { xs: 2, sm: 3 },
                      transition: 'border-color 0.4s ease, background-color 0.4s ease',
                      border: '2px solid transparent',
                      position: 'relative',
                      overflow: 'visible',
                      cursor: 'pointer',
                      textAlign: 'center',
                      height: '100%',
                      maxWidth: { xs: '100%', sm: '350px' },
                      mx: 'auto',
                      '&:hover': {
                        borderColor: '#ffd700',
                        bgcolor: '#0f2832'
                      }
                    }}
                    onClick={() => navigate('/services')}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#ffd700',
                        borderRadius: '50%',
                        width: '80px',
                        height: '80px',
                        mb: 2,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: '#275664',
                          opacity: 0.3,
                          borderRadius: '50%'
                        }}
                      />
                      <Box sx={{
                        '& svg': {
                          fontSize: 45,
                          color: '#275664',
                          position: 'relative',
                          zIndex: 1
                        }
                      }}>
                        {service.icon}
                      </Box>
                    </Box>
                    <Typography variant="h5" sx={{ mt: 2, mb: 2, fontWeight: 'bold', color: 'white' }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                      {service.description}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section (moved) */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 6, fontWeight: 'bold', color: 'white' }}
          >
            Über Uns
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#1a3a44',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    gap: { xs: 2, sm: 3 },
                    transition: 'transform 0.5s ease-out, border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.5s ease-out',
                    border: '2px solid transparent',
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#ffd700',
                      transform: 'translateY(-10px)',
                      bgcolor: '#0f2832',
                      boxShadow: '0 6px 12px rgba(255, 215, 0, 0.12)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: '#ffd700',
                      borderRadius: '50%',
                      width: { xs: '60px', sm: '70px' },
                      height: { xs: '60px', sm: '70px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      position: 'relative',
                      overflow: 'visible'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: '#275664',
                        opacity: 0.3,
                        borderRadius: '50%'
                      }}
                    />
                    <Box sx={{
                      '& svg': {
                        fontSize: { xs: 30, sm: 35 },
                        color: '#275664',
                        position: 'relative',
                        zIndex: 1
                      }
                    }}>
                      {item.icon}
                    </Box>
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        bgcolor: '#275664',
                        color: '#ffd700',
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        fontWeight: 'bold',
                        px: 0.8,
                        py: 0.4,
                        borderRadius: 1,
                        transform: 'rotate(15deg)',
                        border: '1px solid #ffd700',
                        zIndex: 2
                      }}
                    >
                      {item.stat}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        fontWeight: 600,
                        mb: 0.5
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        opacity: 0.8,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        lineHeight: 1.5
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: '#275664' }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 6, fontWeight: 'bold', color: 'white' }}
          >
            Häufig gestellte Fragen
          </Typography>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                bgcolor: '#1a3a44',
                color: 'white',
                '&:before': {
                  display: 'none'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#ffd700' }} />}
                sx={{
                  '&:hover': {
                    bgcolor: '#1a3a44'
                  }
                }}
              >
                <Typography variant="h6">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ opacity: 0.9 }}>{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>
    </div>
  );
};

export default Home;